import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/dom/ajax';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/mapTo';
import * as ResourceActions from 'resources/actions';
import {createdResourceAction, saveResourceChangelistSuccess} from 'resources/actions';
import * as ImageActions from 'pages/Images/actions';
import * as ResourceApi from '../../api/resources';
import {filterOptionsFactory, sortOptionsFactory} from '../../viewOptions/factories';
import * as BreakActions from './actions';
import {
  endRenameStepAction,
  errorCreatingPaletteAction,
  getAllBreakImagesAction,
  getBreakImagesPageAction,
  successCreatingPaletteAction
} from './actions';
import {getAllResourcesAction, getResourcePageAction, receivedAllResourcesAction} from "../../resources/actions";
import {isNoU, toggleSort} from "../../utils/DBUtils";
import {breakImagesPageName} from "./utils";
import {receivedBreakTagsAction} from "./actions";
import {endEditBreakTagsAction} from "./actions";
import {breakTagAddedAction} from "./actions";

const makeBreakImagesFilterOptions = breakId => filterOptionsFactory({
    filters: [{
        field: "imageType",
        value: "original"
    }, {
        field: "usedBy.id",
        value: breakId
    }]
});

const breakImagesSortOptions = sortOptionsFactory('_created');
breakImagesSortOptions.sorts = toggleSort(breakImagesSortOptions.sorts, '_created');

export const makeGetAllBreaksByChallengeIdAction = (challengeId) => {
    const sortOptions = sortOptionsFactory();
    const filterOptions = filterOptionsFactory({
        filters: [{
            field: 'challengeId', value: challengeId
        }]
    });

    return ResourceActions.getAllResourcesAction(ResourceApi.Types.Break, sortOptions, filterOptions);
};

const getBreak = action$ => action$.ofType(BreakActions.Types.REQUEST_BREAK, ResourceActions.Types.SYNCHRONIZED_RESOURCE)
    .mergeMap(action => {
            const sortOptions = sortOptionsFactory();
            const filterOptions = filterOptionsFactory({
                filters: [{
                    field: 'challengeId', value: action.challengeId
                }]
            });

            return ResourceApi.getAll(ResourceApi.Types.Break, sortOptions, filterOptions, action.params)
                .mergeMap(({response: {object}}) => {
                    const masterBreakId = object.find((brk) => brk.platform === "MASTER").id;

                    const actions = [
                        receivedAllResourcesAction(ResourceApi.Types.Break, object),
                        getBreakImagesPageAction(10, 1, masterBreakId), // for ImageUploaderAccordion
                        getAllBreakImagesAction(masterBreakId),         // for BreakImagePicker
                        {
                            type: ResourceActions.Types.RECEIVED_ALL_RESOURCES + "_BREAK",
                            resourceType: action.resourceType,
                            resources: object
                        }
                    ];

                    return Observable.of(...actions);
                })
                .catch(err => Observable.of({type: "ERROR", error: err}));
        }
    );

const saveDisplayOrderEpic = (action$, store) => action$.ofType(BreakActions.Types.START_DISPLAY_ORDER_SAVE)
    .mergeMap(action => {
      const { masterResourceId, stepResourceId } = action;

      const displayOrderSaves = action.displayOrdersAndResourceIds.map(({ displayOrder, resourceId }) => {
        const { type, id } = resourceId;
        const resourceIds = [masterResourceId, stepResourceId, resourceId];

        return ResourceApi.saveField(type, id, 'displayOrder', displayOrder, resourceIds);
      });


      const { selectedChallengeId } = store.getState().editBreak;
      const filterOptions = filterOptionsFactory({
        filters: [{field: 'challengeId', value: selectedChallengeId}]
      });
      const masterReload = () => getAllResourcesAction(ResourceApi.Types.Break, sortOptionsFactory(), filterOptions);

      return Observable.forkJoin(...displayOrderSaves, masterReload);
    });

const didSaveDisplayOrderEpic = (action$, store) => action$.ofType(BreakActions.Types.END_DISPLAY_ORDER_SAVE)
    .filter(action => {
      const { lastAction } = store.getState().editBreak;
      return lastAction === BreakActions.Types.START_DISPLAY_ORDER_SAVE;
    })
    .mapTo(BreakActions.endDisplayOrderSaveAction());

const renamedStepEpic = (action$, store) => action$.ofType(ResourceActions.Types.RESOURCE_FIELD_SAVED)
    .filter(action => Boolean(store.getState().editBreak.renamingStep))
    .mapTo(endRenameStepAction());

const getBreakImagesPageEpic = (action$) => action$.ofType(BreakActions.Types.GET_BREAK_IMAGES_PAGE)
    .map(action => {
        return getResourcePageAction(ResourceApi.Types.Image, action.count, action.page, breakImagesSortOptions, makeBreakImagesFilterOptions(action.breakId), breakImagesPageName);
    });

const updateBreakImagesPageEpic = (action$, store) => action$.ofType(ResourceActions.Types.UPDATE_PAGE_NUMBER_FOR_RESOURCE)
    .filter(action => action.resourceType === breakImagesPageName)
    .map(action => {
      const masterBreakId = store.getState().editBreak.break.MASTER;
      return getResourcePageAction(ResourceApi.Types.Image, 10, action.page, breakImagesSortOptions, makeBreakImagesFilterOptions(masterBreakId), breakImagesPageName);
    });

const refetchBreakImagesEpic = (action$, store) => action$.ofType(ImageActions.Types.UPLOADED_ORIGINAL, ImageActions.Types.REMOVED_IMAGE_FROM_CURRENT_BREAK)
    // only refetch break images if image uploaded was created for another resource, like a break.
    .filter(action => {
      return store.getState().editBreak.break
        && (action.type === ImageActions.Types.UPLOADED_ORIGINAL ? action.image.createdFor : true)
    })
    .mergeMap(action => {
        let editState = store.getState().editBreak;
        return Observable.of(
            getBreakImagesPageAction(editState.lastBreakImagesCount, editState.lastBreakImagesPage, editState.lastBreakImagesId),
            getAllBreakImagesAction(editState.break.MASTER)
        );
    });

const getAllBreakImagesEpic = action$ => action$.ofType(BreakActions.Types.GET_ALL_BREAK_IMAGES)
    .map(({breakId}) => getAllResourcesAction(ResourceApi.Types.Image, breakImagesSortOptions, makeBreakImagesFilterOptions(breakId)));

const setBreakPaletteEpic = (action$) => action$.ofType(BreakActions.Types.SET_BREAK_PALETTE)
    .mergeMap(({palette, breakId}) => {
      return ResourceApi.saveField(ResourceApi.Types.Break,breakId,'colorPalette',palette.id, [{
        type: ResourceApi.Types.Break,
        id: breakId
      }])
      .mergeMap((response) => {
        // remove non color fields
        var colorOnlyObject = {
          missionColor1: palette.brandThemeColor,
          missionColor3: palette.lockedOverlayColor,
          missionColor5: palette.buttonColor,
          missionColor6: palette.brandThemeText,
          missionColor7: palette.resultPageTextBgColor,
          replayColor: palette.replayTextColor,
          questionNavColor: palette.processBarMainBkgdColor,
          questionNavTextColor: palette.progressBarTextColor,
          questionNavCorrectColor: palette.processBarSuccessBkgdColor,
          questionNavIncorrectColor: palette.progressBarFailBkgdColor,
          landingPageHeadlineColor: palette.landingPageHeadlineColor,
          mobileLandingPageHeadlineColor: palette.mobileLandingPageHeadlineColor,
          landingPageIntroColor: palette.landingPageIntroColor,
          mobileLandingPageIntroColor: palette.mobileLandingPageIntroColor,
          termsColor: palette.legalTextColor,
          colorPalette: palette.id,
          sponsor: palette.sponsorNameColor,
          backgroundColor: palette.pageBkgdColor,
          websiteCopyColor: palette.websiteCopyColor,
          mobileWebsiteCopyColor: palette.mobileWebsiteCopyColor
        };


        return Observable.of(
          saveResourceChangelistSuccess(ResourceApi.Types.Break,breakId,colorOnlyObject)
        );
      })
      .catch(err => Observable.of({type: "ERROR", error: err}));

    });

const saveNewPaletteEpic = (action$) => action$.ofType(BreakActions.Types.SAVE_PALETTE)
      .mergeMap(({newPaletteName, breakId}) => {
          return ResourceApi.create(ResourceApi.Types.Palette, {
            name: newPaletteName
          }, [{
            type: ResourceApi.Types.Break,
            id: breakId
          }])
          .mergeMap(({response: {object}}) => {
            return Observable.of(
              successCreatingPaletteAction(),
              createdResourceAction(object, object, [],[],[])
            );
          })
          .catch(err => Observable.of(
              !isNoU(err.xhr) ? errorCreatingPaletteAction(err.response)
              : {type: "ERROR", error: err}));
      });

const addBreakTagEpic = (action$, store) => action$.ofType(BreakActions.Types.ADD_BREAK_TAG)
    .map((action) => {
        const {editBreak, resources} = store.getState();
        let breakResourceId = {type: ResourceApi.Types.Break, id: editBreak.break.MASTER};
        let tagLabels = action.tagIds;

        if (!isNoU(action.tag)) {
            // just created a tag, add to current break labels
            let currentTagIds = resources.resources[editBreak.break.MASTER].breakTagIds;
            if (isNoU(currentTagIds)) {
                currentTagIds = [];
            }
            currentTagIds.push(action.tag.id);

            tagLabels = currentTagIds;
        }

        return ResourceActions.saveResourceFieldAction(
            breakResourceId,
            breakResourceId,
            "",
            "breakTagIds",
            "breakTagIds",
            tagLabels,
            [breakResourceId],
            null,
            null,
            true,
            null,
            [
                breakTagAddedAction()
            ]
        );
    });

const removeBreakTagEpic = (action$, store) => action$.ofType(BreakActions.Types.REMOVE_BREAK_TAG)
    .map((action) => {
        const editBreak = store.getState().editBreak;
        let breakResourceId = {type: ResourceApi.Types.Break, id: editBreak.break.MASTER};
        let brk = store.getState().resources.resources[editBreak.break.MASTER];

        let tagIds = isNoU(brk.breakTagIds) ? [] : brk.breakTagIds.slice();

        let index = tagIds.findIndex((item) => item === action.tagId);

        tagIds.splice(index, 1);

        return ResourceActions.saveResourceFieldAction(
            breakResourceId,
            breakResourceId,
            "",
            "breakTagIds",
            "breakTagIds",
            tagIds,
            [breakResourceId]
        );
    });

export default [
    getBreak,
    saveDisplayOrderEpic,
    didSaveDisplayOrderEpic,
    renamedStepEpic,
    getBreakImagesPageEpic,
    refetchBreakImagesEpic,
    updateBreakImagesPageEpic,
    getAllBreakImagesEpic,
    setBreakPaletteEpic,
    saveNewPaletteEpic,
    // searchBreakTagsEpic,
    removeBreakTagEpic,
    addBreakTagEpic
];



// WEBPACK FOOTER //
// ./public/pages/EditBreak/epics.js